import { useState } from 'react';
import { List } from 'lucide-react';
import type { DataListProps } from '@/types/list.types';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ListHeader } from './ListHeader';
import { ListFilters } from './ListFilters';
import { ListTable } from './ListTable';
import { ListPagination } from './ListPagination';

export function DataList<T>({
  data,
  total,
  isLoading = false,
  error = null,
  columns,
  title,
  titleIcon,
  headerActions,
  showCount = true,
  customHeaderLeft,
  searchInput,
  filterSearch,
  filters,
  activeFiltersCount = 0,
  onResetFilters,
  filtersClassName,
  pagination,
  onPaginationChange,
  pageSizeOptions,
  sortConfig,
  onSortChange,
  rowActions,
  rowClassName,
  onRowClick,
  emptyMessage,
  getRowKey,
  alternativeView,
  mobileCardView: MobileCardView,
  enableReorder = false,
  onReorder,
  reorderPermission,
  embedded = false,
  singleLineHeaders = false,
  viewTogglePosition,
}: DataListProps<T>) {
  const [viewMode, setViewMode] = useState<'table' | 'alternative'>('table');
  const isMobile = useIsMobile();

  const handleSortChange = (sortBy: string, sortDirection: 'ASC' | 'DESC') => {
    onSortChange?.({ sortBy, sortDirection });
  };

  const showAlternativeView = viewMode === 'alternative' && alternativeView;
  const AlternativeViewComponent = alternativeView?.component;

  // Mobile card view takes priority over table when on small screens
  const useMobileCards = isMobile && MobileCardView;

  return (
    <div className={embedded ? (title ? 'tw-py-4' : 'tw-pb-4') : 'tw-container tw-mx-auto tw-px-4 tw-py-2'}>
      {/* Header */}
      {(title || customHeaderLeft || headerActions || alternativeView) && (
        <ListHeader
          title={title}
          icon={titleIcon}
          total={total}
          showCount={showCount}
          customHeaderLeft={customHeaderLeft}
          viewToggle={
            alternativeView && !isMobile ? (
              <div className="tw-flex tw-gap-1 tw-border tw-border-gray-300 tw-rounded tw-p-0.5">
                <button
                  onClick={() => setViewMode('table')}
                  className={`tw-px-2.5 tw-py-1.5 tw-rounded tw-text-sm tw-font-medium tw-transition-colors ${
                    viewMode === 'table'
                      ? 'tw-bg-affinicar-lighter tw-text-affinicar-dark'
                      : 'tw-text-gray-600 hover:tw-bg-gray-100'
                  }`}
                  title="Vue tableau"
                >
                  <List className="tw-w-4 tw-h-4" />
                </button>
                <button
                  onClick={() => setViewMode('alternative')}
                  className={`tw-px-2.5 tw-py-1.5 tw-rounded tw-text-sm tw-font-medium tw-transition-colors ${
                    viewMode === 'alternative'
                      ? 'tw-bg-affinicar-lighter tw-text-affinicar-dark'
                      : 'tw-text-gray-600 hover:tw-bg-gray-100'
                  }`}
                  title={alternativeView.label}
                >
                  <alternativeView.icon className="tw-w-4 tw-h-4" />
                </button>
              </div>
            ) : undefined
          }
          actions={headerActions}
          viewTogglePosition={viewTogglePosition}
        />
      )}

      {/* Filtres */}
      {(searchInput || filterSearch || filters) && (
        <div className={filtersClassName}>
          <ListFilters
            activeFiltersCount={activeFiltersCount}
            onReset={onResetFilters}
            search={filterSearch}
            advancedFilters={filterSearch ? filters : undefined}
          >
            {!filterSearch && (
              <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3 md:tw-gap-4">
                {searchInput && <div className="tw-w-full md:tw-w-48 tw-flex-shrink-0">{searchInput}</div>}
                {filters && <div className="tw-flex-shrink-0">{filters}</div>}
              </div>
            )}
          </ListFilters>
        </div>
      )}

      {/* Contenu */}
      {useMobileCards ? (
        <MobileCardView data={data} onRowClick={onRowClick} />
      ) : showAlternativeView && AlternativeViewComponent ? (
        <AlternativeViewComponent data={data} onRowClick={onRowClick} />
      ) : (
        <ListTable
          data={data}
          columns={columns}
          isLoading={isLoading}
          error={error}
          sortConfig={sortConfig}
          onSortChange={handleSortChange}
          rowActions={rowActions}
          rowClassName={rowClassName}
          onRowClick={onRowClick}
          emptyMessage={emptyMessage}
          getRowKey={getRowKey}
          enableReorder={enableReorder}
          onReorder={onReorder}
          reorderPermission={reorderPermission}
          singleLineHeaders={singleLineHeaders}
        />
      )}

      {/* Pagination */}
      {!isLoading && !error && total > 0 && (
        <ListPagination
          pagination={pagination}
          total={total}
          onChange={onPaginationChange}
          pageSizeOptions={pageSizeOptions}
        />
      )}
    </div>
  );
}
